import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useSound } from '@/hooks/useSound';
import { MessageCircle, X } from 'lucide-react';

interface QuickMessage {
  emoji: string;
  text: string;
}

interface ChatBubble {
  id: string;
  playerId: string;
  playerName: string;
  message: QuickMessage;
  timestamp: number;
}

interface QuickChatProps {
  roomId: string;
  playerId: string;
  playerName: string;
}

const quickMessages: QuickMessage[] = [
  { emoji: '👍', text: 'Nice!' },
  { emoji: '😅', text: 'Oops' },
  { emoji: '🤔', text: 'Thinking...' },
  { emoji: '🎯', text: 'Good pick!' },
  { emoji: '⚡', text: 'Go fast!' },
  { emoji: '🎉', text: "Let's go!" },
  { emoji: '😱', text: 'Oh no!' },
  { emoji: '🔥', text: 'Fire!' },
];

export default function QuickChat({ roomId, playerId, playerName }: QuickChatProps) {
  const [chatBubbles, setChatBubbles] = useState<ChatBubble[]>([]);
  const [cooldown, setCooldown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { playSound } = useSound();

  // Subscribe to chat messages via Supabase Realtime broadcast
  useEffect(() => {
    const channel = supabase
      .channel(`quick-chat-${roomId}`)
      .on('broadcast', { event: 'chat' }, (payload) => {
        const bubble: ChatBubble = {
          id: `${Date.now()}-${payload.payload.playerId}`,
          playerId: payload.payload.playerId,
          playerName: payload.payload.playerName,
          message: payload.payload.message,
          timestamp: Date.now(),
        };
        
        setChatBubbles(prev => [...prev, bubble]);
        if (payload.payload.playerId !== playerId) {
          playSound('pop', 0.3);
        }
        
        // Remove after 3 seconds
        setTimeout(() => {
          setChatBubbles(prev => prev.filter(b => b.id !== bubble.id));
        }, 3000);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [roomId, playerId, playSound]);

  const sendMessage = async (msg: QuickMessage) => {
    if (cooldown) return;
    
    setCooldown(true);
    playSound('select', 0.3);
    setIsExpanded(false); // Close on mobile after sending
    
    await supabase.channel(`quick-chat-${roomId}`).send({
      type: 'broadcast',
      event: 'chat',
      payload: {
        playerId,
        playerName,
        message: msg,
      },
    });
    
    setTimeout(() => setCooldown(false), 2000);
  };

  return (
    <>
      {/* Chat Bubbles - Positioned at top on mobile, bottom on desktop */}
      <div className="fixed top-20 left-4 right-4 sm:top-auto sm:bottom-32 sm:left-4 sm:right-auto z-40 space-y-2 pointer-events-none">
        <AnimatePresence>
          {chatBubbles.map((bubble) => (
            <motion.div
              key={bubble.id}
              initial={{ y: -20, opacity: 0, scale: 0.8 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: -20, opacity: 0, scale: 0.8 }}
              className="glass-card px-3 py-2 rounded-xl shadow-lg flex items-center gap-2 max-w-[200px] sm:max-w-none"
            >
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center text-[10px] font-bold text-primary-foreground shrink-0">
                {bubble.playerName[0]}
              </div>
              <span className="text-xl">{bubble.message.emoji}</span>
              <span className="text-xs font-medium text-foreground truncate">{bubble.message.text}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Mobile: Collapsible toggle button */}
      <div className="sm:hidden fixed bottom-4 right-4 z-40">
        <AnimatePresence mode="wait">
          {isExpanded ? (
            <motion.div
              key="expanded"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="glass-card rounded-2xl p-3"
            >
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-muted-foreground font-bold">Quick Chat</p>
                <button
                  onClick={() => setIsExpanded(false)}
                  className="w-6 h-6 rounded-full bg-muted flex items-center justify-center"
                >
                  <X className="w-3 h-3 text-muted-foreground" />
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {quickMessages.map((msg) => (
                  <motion.button
                    key={msg.text}
                    onClick={() => sendMessage(msg)}
                    disabled={cooldown}
                    className={`w-10 h-10 rounded-lg bg-secondary hover:bg-accent 
                               flex items-center justify-center text-xl
                               ${cooldown ? 'opacity-50' : ''}`}
                    whileTap={{ scale: 0.9 }}
                  >
                    {msg.emoji}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.button
              key="collapsed"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={() => setIsExpanded(true)}
              className="w-12 h-12 rounded-full glass-card flex items-center justify-center shadow-lg"
              whileTap={{ scale: 0.9 }}
            >
              <MessageCircle className="w-5 h-5 text-primary" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop: Always visible grid */}
      <motion.div 
        className="hidden sm:block fixed bottom-4 right-4 z-40"
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5 }}
      >
        <div className="glass-card rounded-xl p-3">
          <p className="text-xs text-muted-foreground font-bold mb-2 text-center">Quick Chat</p>
          <div className="grid grid-cols-4 gap-1">
            {quickMessages.map((msg) => (
              <motion.button
                key={msg.text}
                onClick={() => sendMessage(msg)}
                disabled={cooldown}
                className={`w-10 h-10 rounded-lg bg-secondary hover:bg-accent 
                           flex items-center justify-center text-xl
                           transition-all hover:scale-110
                           ${cooldown ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={{ scale: cooldown ? 1 : 1.15 }}
                whileTap={{ scale: cooldown ? 1 : 0.9 }}
                title={msg.text}
              >
                {msg.emoji}
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
}
